/// @ts-check

import React, { useMemo, useState } from "react";
import { Button, ButtonGroup, Table } from "react-bootstrap";
import AddAccount from "./CustomersCreate/AddAccounts";
import AddScopes from "./CustomersCreate/AddScopes";
import Badges from "src/components/Badges/Badges";
import formatNumber from "src/utils/formatNumber";
import usePermissions from "src/components/CustomHooks/usePermissions";
import WbTwilight from "@mui/icons-material/WbTwilight";
import InfoIcon from "@mui/icons-material/Info";
import { confirmAlert } from "react-confirm-alert";
import { Tooltip } from "@mui/material";
import { SunsetStoreDialog } from "./SunsetStoreDialog/SunsetStoreDialog";
import dialogCss from "src/styles/dialog.module.css";
import { SunsetStoreActionBar } from "./SunsetStoreActionBar/SunsetStoreActionBar";
import { ServerScopeName } from "./api-v2/types/server-vendor-account";
import { EMPTY_OBJECT } from "src/utils/empty-values";
import {
  ACTIVE_ITEM_STATUS,
  INACTIVE_ITEM_STATUS,
} from "src/pages/UserDashboard/WorkPackets/activation-status/activation-status-utils";
import { parseMaybeDateString } from "src/utils/parse-maybe-date-string";

/** @import { CSSProperties, FC } from "react"; */
/** @import { ServerVendorAccount } from "./api-v2/types/server-vendor-account"; */
/** @import { ActivationStatus } from "src/pages/UserDashboard/WorkPackets/activation-status/activation-status-types"; */

/** @type {Record<ServerScopeName, string>} */
const scopeLabels = {
  [ServerScopeName.PRICE_CLAIMS]: "PC",
  [ServerScopeName.SHORTAGES]: "SH",
  [ServerScopeName.CHARGEBACKS]: "CB",
  [ServerScopeName.ACCRUALS]: "ACC",
  [ServerScopeName.VENDOR_RETURNS]: "VR",
};

const getScopeLabel = (/** @type {keyof typeof scopeLabels} */ scope) => scopeLabels[scope] || "";

/** @type {FC<{ status: "sunset" | "inactive" }>} */
const StoreActivationStatusBanner = ({ status }) => (
  <div
    style={{
      backgroundColor: status === "sunset" ? "rgb(255, 244, 229)" : "rgb(253, 237, 237)",
      padding: "10px 20px",
      borderRadius: "5px",
      display: "flex",
      justifyContent: "space-between",
      whiteSpace: "nowrap",
      gap: "10px",
    }}
  >
    <span>
      This store is <b>{status === "sunset" ? <>sunset</> : <>deactivated</>}</b>
    </span>
    <span>
      <Tooltip
        arrow
        slotProps={{ tooltip: { style: { transform: "scale(1.5)" } } }}
        title={
          status === "sunset" ? (
            <>
              This store is sunset, meaning it is no longer active but will continue to process existing work packets
              and cases.
            </>
          ) : (
            <>This store is deactivated, meaning our system will no longer process any work packets or cases.</>
          )
        }
      >
        <InfoIcon color="action" />
      </Tooltip>
    </span>
  </div>
);

/** @type {(store: ServerVendorAccount) =>  ActivationStatus} */
function computeStoreActivationStatus(store) {
  if (store.STATUS === "inactive") return INACTIVE_ITEM_STATUS;
  if (store.DEACTIVATION_DATE) {
    return {
      state: "sunset",
      deactivationDate: parseMaybeDateString(store.DEACTIVATION_DATE) ?? undefined,
    };
  }
  return ACTIVE_ITEM_STATUS;
}

/** @type {(stores: ServerVendorAccount[]) => Map<ServerVendorAccount, ActivationStatus>} */
function useStoreActivationStatusMap(stores) {
  return useMemo(() => new Map(stores.map(store => [store, computeStoreActivationStatus(store)])), [stores]);
}

/** @type{CSSProperties} */
const storeActionButtonStyles = { flexGrow: 1, borderRadius: "5px", whiteSpace: "nowrap", transitionProperty: "all" };

/**
 * @typedef {{
 *  isRefreshing?: boolean;
 *  vendorAccounts: ServerVendorAccount[];
 *  ShippingData: { label: string; value: string }[];
 *  refetchVendorAccounts: () => void;
 *  auditAccounts: { label: string; value: string }[];
 *  chargebackProcessorData: { label: string; value: string }[];
 * }} ViewAccountsTableProps
 */

/** @type {FC<ViewAccountsTableProps>} */
const ViewAccountsTable = ({
  vendorAccounts,
  ShippingData,
  refetchVendorAccounts,
  auditAccounts,
  chargebackProcessorData,
}) => {
  const { hasPermission } = usePermissions();

  // <AddAccount /> uses empty object as empty state
  const [editAccountTarget, setEditAccountTarget] = useState(/** @type {ServerVendorAccount | {}} */ (EMPTY_OBJECT));

  // <AddScopes /> uses empty string as empty state
  const [scopeDetailsTargetAccountId, setScopeDetailsTargetAccountId] = useState(/** @type {number | ""} */ (""));

  /** @type {(account: ServerVendorAccount) => void} */
  const onViewStoreClick = account => {
    setEditAccountTarget(currentSelection => (currentSelection === account ? EMPTY_OBJECT : account));
    setScopeDetailsTargetAccountId("");
  };

  /** @type {(accountId: number) => void} */
  const onViewScopesClick = accountId => {
    setScopeDetailsTargetAccountId(currentSelection => (currentSelection === accountId ? "" : accountId));
    setEditAccountTarget(EMPTY_OBJECT);
  };

  const storeActivationStatusMap = useStoreActivationStatusMap(vendorAccounts);
  const activeStores = useMemo(
    () => vendorAccounts.filter(store => storeActivationStatusMap.get(store)?.state === "active"),
    [vendorAccounts, storeActivationStatusMap],
  );

  const [sunsetSelection, setSunsetSelection] = useState(() => /** @type {Set<ServerVendorAccount>} */ (new Set()));

  const onSuccessfulScopeUpdate = () => {
    setSunsetSelection(new Set());
    refetchVendorAccounts();
  };

  const toggleStoreSunsetSelection = (/** @type {ServerVendorAccount} */ store) => {
    setSunsetSelection(currentSelection => {
      const newSelection = new Set(currentSelection);
      if (newSelection.has(store)) {
        newSelection.delete(store);
      } else {
        newSelection.add(store);
      }
      return newSelection;
    });
  };
  const onSunsetSuccess = () => {
    setSunsetSelection(new Set());
    refetchVendorAccounts();
  };

  const onSunsetConfirm = () => {
    setEditAccountTarget(EMPTY_OBJECT);
    setScopeDetailsTargetAccountId("");

    return confirmAlert({
      customUI: ({ onClose }) => {
        const stores = Array.from(sunsetSelection);
        return <SunsetStoreDialog stores={stores} onSuccess={onSunsetSuccess} onClose={onClose} />;
      },
      closeOnClickOutside: false,
      closeOnEscape: false,
      overlayClassName: dialogCss.dialogOverlay,
    });
  };

  const onSunsetClear = () => {
    setSunsetSelection(new Set());
  };

  const onSunsetSelectAll = () => {
    setSunsetSelection(new Set(activeStores));
  };

  return (
    <div className="table-container">
      <SunsetStoreActionBar
        active={sunsetSelection.size > 0}
        count={sunsetSelection.size}
        onClear={onSunsetClear}
        onSelectAll={onSunsetSelectAll}
        onConfirm={onSunsetConfirm}
      />
      <Table>
        <thead>
          <tr>
            <th>Store ID</th>
            <th>Name</th>
            <th>Scopes - Activation Date</th>
            <th>GMV</th>
            <th>Tech User</th>
            <th>Data Pull Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {vendorAccounts?.map(account => (
            <React.Fragment key={account.ID}>
              <tr>
                <td>{`S${account?.ID}`}</td>
                <td>{account?.NAME || "N/A"}</td>
                <td>
                  <table>
                    <tbody>
                      {account.SCOPES?.length ? (
                        account.SCOPES.map(scope => (
                          <tr key={scope.scope_name}>
                            <td>
                              <Badges text={getScopeLabel(scope.scope_name)} />
                            </td>
                            <td>-</td>
                            <td>
                              <p>{scope.activation_date}</p>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2}>
                            <span className="badge text-bg-secondary text-white">No scopes found</span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </td>
                <td>{account?.GMV ? formatNumber(Number(account.GMV)) : "N/A"}</td>
                <td>{account?.DEFAULT_AVC_USER || "N/A"}</td>
                <td>{account?.AVC_DATA_PULL_STATUS || "N/A"}</td>
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
                      <Button
                        variant={
                          "ID" in editAccountTarget && account.ID == editAccountTarget.ID ? "dark" : "outline-dark"
                        }
                        style={storeActionButtonStyles}
                        onClick={() => onViewStoreClick(account)}
                      >
                        View Store
                      </Button>
                      <Button
                        variant={account.ID === scopeDetailsTargetAccountId ? "dark" : "outline-dark"}
                        style={storeActionButtonStyles}
                        onClick={() => onViewScopesClick(account.ID)}
                      >
                        {account.SCOPES?.length === 0 ? "Add Scopes" : "View Scopes"}
                      </Button>
                    </div>
                    {storeActivationStatusMap.get(account)?.state === "inactive" ? (
                      <StoreActivationStatusBanner status="inactive" />
                    ) : storeActivationStatusMap.get(account)?.state === "sunset" ? (
                      <StoreActivationStatusBanner status="sunset" />
                    ) : (
                      <ButtonGroup>
                        {hasPermission(["admin:*", "user:client_config:write:*"]) && (
                          <Button
                            variant={sunsetSelection.has(account) ? "danger" : "outline-danger"}
                            style={storeActionButtonStyles}
                            onClick={() => toggleStoreSunsetSelection(account)}
                          >
                            {sunsetSelection.has(account) ? (
                              <>
                                <WbTwilight style={{ color: "currentColor", marginRight: "5px" }} />
                                Selected for sunsetting
                              </>
                            ) : (
                              <>
                                <WbTwilight style={{ color: "currentColor", marginRight: "5px" }} />
                                Sunset this store
                              </>
                            )}
                          </Button>
                        )}
                      </ButtonGroup>
                    )}
                  </div>
                </td>
              </tr>
              {/* Use ID comparisons, as objects will be refreshed on successful changes */}
              {"ID" in editAccountTarget && account.ID == editAccountTarget.ID && (
                <tr>
                  <td colSpan={10}>
                    <AddAccount
                      vendorId={account.VENDOR_ID}
                      handleShowAccountsForm={undefined}
                      tableDataEdit={editAccountTarget}
                      avcData={auditAccounts}
                      getData={refetchVendorAccounts}
                      mode="edit"
                      chargebackProcessorData={chargebackProcessorData}
                      ShippingData={ShippingData}
                    />
                  </td>
                </tr>
              )}
              {account.ID === scopeDetailsTargetAccountId && (
                <tr>
                  <td colSpan={10}>
                    <AddScopes
                      onSuccessfulScopeUpdate={onSuccessfulScopeUpdate}
                      accountId={scopeDetailsTargetAccountId}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ViewAccountsTable;
